if (!customElements.get('ffs-about-gallery')) {
  customElements.define('ffs-about-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (!this.hasAttribute('data-auto-scroll') || window.Shopify?.designMode) return;
      this.track = this.querySelector('.ffa-track');
      if (!this.track) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.paused = false;
      this.last = 0;
      const options = {signal: this.controller.signal};
      const pause = () => { this.paused = true; this.track.style.scrollSnapType = ''; this.position = null; };
      const resume = () => { this.paused = false; this.last = 0; this.position = null; };
      this.addEventListener('pointerenter', pause, options);
      this.addEventListener('pointerleave', resume, options);
      this.addEventListener('focusin', pause, options);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) resume(); }, options);
      this.addEventListener('pointerdown', () => { this.userStopped = true; pause(); }, options);
      this.addEventListener('keydown', () => { this.userStopped = true; pause(); }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.last = 0;
        if (this.visible && !this.frame) this.frame = requestAnimationFrame(time => this.tick(time));
      });
      this.observer.observe(this);
    }
    tick(time) {
      this.frame = null;
      if (!this.isConnected || !this.visible) return;
      if (this.last && !this.paused && !this.userStopped && !this.motion.matches && !document.hidden && time > (this.waitUntil || 0)) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max > 0) {
          this.track.style.scrollSnapType = 'none';
          if (this.position == null) this.position = this.track.scrollLeft;
          if (this.position >= max - 2) {
            this.track.scrollLeft = 0;
            this.position = 0;
            this.waitUntil = time + 500;
          } else {
            this.position += Math.min(time - this.last, 50) / 60;
            this.track.scrollLeft = this.position;
          }
        }
      }
      this.last = time;
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}
if (!customElements.get('ffs-about-video')) {
  customElements.define('ffs-about-video', class extends HTMLElement {
    static active = null;
    static resolveLink(value) {
      let url;
      try { url = new URL(value); } catch { return null; }
      if (url.protocol !== 'https:' || url.username || url.password) return null;
      const host = url.hostname.toLowerCase();
      if (['facebook.com', 'www.facebook.com', 'm.facebook.com', 'web.facebook.com'].includes(host)) {
        if (!/^\/(?:reel\/\d+\/?|watch\/?|[^/]+\/videos\/[^/]+\/?|video\.php)$/i.test(url.pathname)) return null;
        if (/^\/(watch\/?|video\.php)$/i.test(url.pathname) && !url.searchParams.get('v')) return null;
        const embed = new URL('https://www.facebook.com/plugins/video.php');
        embed.searchParams.set('href', url.href);
        embed.searchParams.set('show_text', 'false');
        embed.searchParams.set('width', '500');
        return {type:'embed',src:embed.href,provider:'facebook'};
      }
      if (['instagram.com', 'www.instagram.com'].includes(host)) {
        const match = url.pathname.match(/^\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)\/?$/);
        if (!match) return null;
        const kind = match[1] === 'reels' ? 'reel' : match[1];
        return {type:'embed',src:`https://www.instagram.com/${kind}/${match[2]}/embed/`,provider:'instagram'};
      }
      if (/\.(mp4|webm)$/i.test(url.pathname)) return {type:'file',src:url.href};
      return null;
    }
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.cover = this.querySelector('[data-video-open]');
      this.area = this.querySelector('.ffa-player-area');
      this.mount = this.querySelector('.ffa-player-mount');
      this.status = this.querySelector('.ffa-video-status');
      const options = {signal:this.controller.signal};
      this.cover?.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-video-close]')?.addEventListener('click', () => this.close(true), options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.hasAttribute('data-playing')) { event.preventDefault(); this.close(true); }
      }, options);
      document.addEventListener('shopify:block:deselect', event => { if (event.target.contains(this)) this.close(); }, options);
    }
    open() {
      if (!this.cover || this.hasAttribute('data-playing')) return;
      this.constructor.active?.close();
      this.constructor.active = this;
      this.setAttribute('data-playing','');
      this.cover.hidden = true;
      this.cover.setAttribute('aria-expanded','true');
      this.area.hidden = false;
      this.status.hidden = true;
      const template = this.querySelector('template[data-uploaded-video]');
      if (template) {
        this.mount.append(template.content.cloneNode(true));
        this.prepareVideo(this.mount.querySelector('video'));
        return;
      }
      const target = this.constructor.resolveLink(this.dataset.videoUrl);
      if (!target) { this.showError(this.dataset.unsupported); return; }
      if (target.type === 'file') {
        const video = document.createElement('video');
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        video.src = target.src;
        this.mount.append(video);
        this.prepareVideo(video);
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.src = target.src;
      iframe.title = this.dataset.playLabel;
      iframe.allowFullscreen = true;
      iframe.allow = 'fullscreen; picture-in-picture; encrypted-media';
      iframe.addEventListener('error', () => this.showError(this.dataset.loadError), {signal:this.controller.signal});
      this.mount.append(iframe);
      iframe.focus();
    }
    prepareVideo(video) {
      if (!video) { this.showError(this.dataset.loadError); return; }
      video.setAttribute('aria-label',this.dataset.playLabel);
      video.tabIndex = 0;
      video.preload = 'none';
      video.addEventListener('error', () => this.showError(this.dataset.loadError), {once:true});
      video.focus();
      const attempt = video.play();
      attempt?.catch(error => { if (error.name !== 'NotAllowedError' && error.name !== 'AbortError' && this.hasAttribute('data-playing')) this.showError(this.dataset.loadError); });
    }
    showError(message) {
      this.status.textContent = message;
      this.status.hidden = false;
      this.querySelector('[data-video-close]')?.focus();
    }
    close(restoreFocus=false) {
      const video = this.mount?.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      }
      this.mount?.replaceChildren();
      this.removeAttribute('data-playing');
      if (this.area) this.area.hidden = true;
      if (this.cover) { this.cover.hidden = false; this.cover.setAttribute('aria-expanded','false'); }
      if (this.constructor.active === this) this.constructor.active = null;
      if (restoreFocus) this.cover?.focus();
    }
    disconnectedCallback() { this.close(); this.controller?.abort(); this.controller = null; }
  });
}
